/**
 * Notifications tab on the contract editor page (R5 / ADR-0031).
 *
 * Reads `notifications.list_by_contract` and presents the materialized
 * reminder rows as GROUPED reminders (REGTA-51 / ADR-0080): a rule with
 * multiple lead times (e.g. lease_end at 3mo/1mo/1wk) renders as one
 * item exposing its full schedule — the imminent occurrence surfaced,
 * the rest behind a collapsible. From the triggered state the whole
 * group can be snoozed or handled in one action; individual occurrences
 * keep their own acknowledge / edit-date / delete controls.
 *
 * Empty state: templates that yield zero rules (e.g. Bostadshyra
 * Tillsvidare) render an explanatory banner + "Add custom reminder" CTA.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlarmClock,
  CalendarClock,
  Check,
  ChevronDown,
  MoreHorizontal,
  Trash2,
} from 'lucide-react';
import {
  tenantApi,
  type ContractNotification,
  type NotificationKind,
  type NotificationLead,
  type NotificationPhase,
  type NotificationPreview,
} from '../api.js';
import {
  groupReminders,
  reminderRowLabel,
  type ReminderGroup,
} from '../lib/reminder-groups.js';
import { useInvalidate, useTenantQuery } from '../query.js';

const KIND_LABELS: Record<NotificationKind, string> = {
  termination_notice: 'Uppsägning',
  negotiation_window: 'Förhandlingsfönster',
  lease_end: 'Hyresavtal löper ut',
  agreement_end: 'Förhandlingsordning löper ut',
  signature_overdue: 'Inväntar signering',
  custom: 'Egen påminnelse',
};

const LEAD_LABELS: Record<NotificationLead, string> = {
  '3mo': '3 månader före',
  '1mo': '1 månad före',
  '1wk': '1 vecka före',
  custom: 'Egen tidpunkt',
};

const SNOOZE_OPTIONS: Array<{ label: string; days: number }> = [
  { label: '1 dag', days: 1 },
  { label: '3 dagar', days: 3 },
  { label: '1 vecka', days: 7 },
];

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

function relativeDays(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const diff = Math.round((d.getTime() - now.getTime()) / 86_400_000);
  if (diff === 0) return 'idag';
  if (diff > 0) return `om ${diff} dagar`;
  return `${Math.abs(diff)} dagar sedan`;
}

function groupHeading(g: ReminderGroup<ContractNotification>): string {
  return g.sharedLabel ?? KIND_LABELS[g.kind];
}

/**
 * Per-occurrence line label inside a group: an occurrence-specific
 * meta.label wins (in-flight escalation steps), unless it just repeats
 * the group heading — then the lead time is the useful distinction.
 */
function occurrenceLabel(
  g: ReminderGroup<ContractNotification>,
  row: ContractNotification,
): string {
  const own = reminderRowLabel(row);
  if (own && own !== groupHeading(g)) return own;
  return LEAD_LABELS[row.leadLabel];
}

export function NotificationsTab({ contractId }: { contractId: string }) {
  const invalidate = useInvalidate();
  const q = useTenantQuery(
    'notifications.list_by_contract',
    (cid) =>
      tenantApi.notifications.list_by_contract({ contract_id: cid as string }),
    [contractId],
    { revalidateOnFocus: false },
  );
  // Preview: what materialization will produce at the next phase
  // transition. Refetches on focus so the tab reflects fresh values
  // after a save in the main editor.
  const previewQ = useTenantQuery(
    'notifications.preview',
    (cid) =>
      tenantApi.notifications.preview({ contract_id: cid as string }),
    [contractId],
    { revalidateOnFocus: true },
  );

  const [showCustom, setShowCustom] = useState(false);

  const reload = async () => {
    await Promise.all([
      invalidate('notifications.list_by_contract', contractId),
      invalidate('notifications.preview', contractId),
      invalidate('notifications.list_due'),
      invalidate('notifications.list_upcoming'),
    ]);
  };

  const notifications = q.data?.notifications ?? [];
  const previews = previewQ.data?.previews ?? [];
  const { activeGroups, handledGroups } = useMemo(() => {
    const groups = groupReminders(notifications);
    return {
      activeGroups: groups.filter((g) => !g.allHandled),
      handledGroups: groups.filter((g) => g.allHandled),
    };
  }, [notifications]);

  // Group previews by phase so we can label each group with its
  // materialization trigger ("vid signering" vs "när avtalet skickas
  // för signering"). Phases the contract has already moved past come
  // back filtered out from the server.
  const previewsByPhase = useMemo(() => {
    const groups: Record<NotificationPhase, NotificationPreview[]> = {
      in_flight: [],
      post_signing: [],
    };
    for (const p of previews) groups[p.phase].push(p);
    return groups;
  }, [previews]);

  if (q.isLoading) {
    return <div className="p-6 text-xs text-stone-400">Laddar påminnelser…</div>;
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-medium text-stone-900">Påminnelser</h2>
          <p className="mt-1 text-xs text-stone-500">
            Automatiska påminnelser kring viktiga datum för avtalet. Flera
            tidpunkter för samma händelse visas som en påminnelse med sitt
            schema. Du kan lägga till egna, justera när en påminnelse visas,
            snooza, eller bocka av som hanterad.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCustom(true)}
          className="rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:border-stone-400 hover:bg-stone-50"
        >
          + Lägg till påminnelse
        </button>
      </header>

      {notifications.length === 0 && previews.length === 0 && (
        <div className="rounded border border-dashed border-stone-300 bg-stone-50 p-6 text-center">
          <p className="text-sm text-stone-700">Inga påminnelser är inplanerade.</p>
          <p className="mt-1 text-xs text-stone-500">
            Den valda mallen ger inga automatiska livscykelpåminnelser för det
            här avtalet (t.ex. tillsvidareavtal utan slutdatum). Lägg gärna
            till egna påminnelser om något specifikt datum du vill bli
            informerad om.
          </p>
        </div>
      )}

      {activeGroups.length > 0 && (
        <ReminderGroupList groups={activeGroups} onChanged={reload} />
      )}

      {previewsByPhase.post_signing.length > 0 && (
        <PreviewSection
          previews={previewsByPhase.post_signing}
          heading="Schemaläggs vid signering"
          hint="Dessa skapas automatiskt när avtalet är signerat. Ändra fälten i avtalet för att justera datumen."
        />
      )}

      {previewsByPhase.in_flight.length > 0 && (
        <PreviewSection
          previews={previewsByPhase.in_flight}
          heading="Schemaläggs när avtalet skickas för signering"
          hint="Dessa skapas när avtalet skickas till motparten — uppföljning om signering dröjer."
        />
      )}

      {handledGroups.length > 0 && (
        <details className="text-xs text-stone-500">
          <summary className="cursor-pointer py-1 font-medium uppercase tracking-wider hover:text-stone-700">
            Hanterade ({handledGroups.length})
          </summary>
          <div className="mt-2">
            <ReminderGroupList groups={handledGroups} onChanged={reload} />
          </div>
        </details>
      )}

      {showCustom && (
        <AddCustomModal
          contractId={contractId}
          onClose={() => setShowCustom(false)}
          onAdded={async () => {
            setShowCustom(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function PreviewSection({
  previews,
  heading,
  hint,
}: {
  previews: NotificationPreview[];
  heading: string;
  hint: string;
}) {
  return (
    <section className="flex flex-col gap-2">
      <header className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-medium uppercase tracking-wider text-stone-500">
          {heading}
        </h3>
        <span className="text-[11px] text-stone-400">
          förhandsvisning · ej än skapad
        </span>
      </header>
      <p className="text-[11px] text-stone-500">{hint}</p>
      <div className="overflow-hidden rounded border border-dashed border-stone-300 bg-stone-50">
        <table className="w-full text-xs">
          <thead className="bg-stone-100 text-left uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-3 py-2 font-medium">Påminnelse</th>
              <th className="px-3 py-2 font-medium">Datum</th>
              <th className="px-3 py-2 font-medium">Knyts till</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200 text-stone-600">
            {previews.map((p) => (
              <tr key={`${p.ruleKey}-${p.fireAt}`}>
                <td className="px-3 py-2 font-medium text-stone-700">{p.label}</td>
                <td className="px-3 py-2">
                  {formatDate(p.fireAt)}
                  <span className="ml-2 text-stone-400">
                    ({relativeDays(p.fireAt)})
                  </span>
                </td>
                <td className="px-3 py-2 text-stone-500">
                  {formatDate(p.triggerAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** Grouped reminder cards — exported for SSR render tests. */
export function ReminderGroupList({
  groups,
  onChanged,
}: {
  groups: Array<ReminderGroup<ContractNotification>>;
  onChanged: () => Promise<void>;
}) {
  return (
    <div className="flex flex-col gap-3">
      {groups.map((g) => (
        <ReminderGroupCard key={g.key} group={g} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ReminderGroupCard({
  group,
  onChanged,
}: {
  group: ReminderGroup<ContractNotification>;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const handled = group.allHandled;
  const multi = group.rows.length > 1;
  const surfaced = group.current ?? group.next;

  // Refetch even on failure: the group's row ids can go stale under an
  // open tab (signing.prolong / enable_for_contract re-create rows), and
  // a refetch heals the ids for the retry.
  const runGroup = async (fn: () => Promise<unknown>) => {
    setErr(null);
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      await onChanged().catch(() => {});
      setBusy(false);
    }
  };
  const handleAll = () =>
    runGroup(() =>
      tenantApi.notifications.acknowledge_group({
        notification_ids: group.active.map((r) => r.id),
      }),
    );
  const snooze = (days: number) =>
    runGroup(() =>
      tenantApi.notifications.snooze({
        notification_ids: group.due.map((r) => r.id),
        snooze_until: new Date(Date.now() + days * 86_400_000).toISOString(),
      }),
    );

  return (
    <div
      data-testid="reminder-group"
      className={
        'flex flex-col gap-3 rounded-lg border bg-white p-4 ' +
        (handled
          ? 'border-stone-200 opacity-70'
          : group.isDue
            ? 'border-amber-300'
            : 'border-stone-200')
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className={
              'text-sm font-medium ' +
              (handled ? 'text-stone-400' : 'text-stone-900')
            }
          >
            {groupHeading(group)}
          </span>
          {multi && (
            <span className="text-[11px] uppercase tracking-wider text-stone-400">
              {group.rows.length} påminnelser · gäller {formatDate(group.triggerAt)}
            </span>
          )}
        </div>
        <div className="shrink-0 text-xs text-stone-500">
          {handled ? (
            <span>hanterad</span>
          ) : group.isDue ? (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
              visas nu
            </span>
          ) : (
            group.next && <span>{relativeDays(group.next.fireAt)}</span>
          )}
        </div>
      </div>

      {!handled && surfaced && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone-600">
          <span className="flex items-center gap-2">
            <CalendarClock className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
            <span className="font-medium">{occurrenceLabel(group, surfaced)}</span>
            <span>{formatDate(surfaced.fireAt)}</span>
            <span className="text-stone-400">({relativeDays(surfaced.fireAt)})</span>
          </span>
          {group.current && group.next && (
            <span className="text-stone-500">
              Nästa: {occurrenceLabel(group, group.next)} ·{' '}
              {formatDate(group.next.fireAt)}
            </span>
          )}
        </div>
      )}

      {(() => {
        const schedule = group.rows.map((row) => (
          <OccurrenceRow key={row.id} group={group} row={row} onChanged={onChanged} />
        ));
        return multi ? (
          <details className="group/schedule">
            <summary className="flex cursor-pointer items-center gap-1 text-xs font-medium text-stone-500 hover:text-stone-700 [&::-webkit-details-marker]:hidden">
              <ChevronDown
                className="h-3.5 w-3.5 transition-transform group-open/schedule:rotate-180"
                aria-hidden="true"
              />
              Visa hela schemat ({group.rows.length})
            </summary>
            <div className="mt-1 flex flex-col">{schedule}</div>
          </details>
        ) : (
          <div className="flex flex-col">{schedule}</div>
        );
      })()}

      {group.isDue && (
        <div className="flex flex-wrap gap-2 border-t border-stone-100 pt-3">
          <button
            type="button"
            disabled={busy}
            onClick={handleAll}
            className="inline-flex items-center gap-1.5 rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {group.active.length > 1
              ? `Hantera alla (${group.active.length})`
              : 'Markera som hanterad'}
          </button>
          <SnoozeMenu disabled={busy} onSnooze={snooze} />
        </div>
      )}

      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
          Åtgärden misslyckades: {err}
        </div>
      )}
    </div>
  );
}

function SnoozeMenu({
  disabled,
  onSnooze,
}: {
  disabled: boolean;
  onSnooze: (days: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-1.5 rounded border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
      >
        <AlarmClock className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
        Snooza
        <ChevronDown className="h-3 w-3 text-stone-400" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-40 mb-1.5 w-32 overflow-hidden rounded-md border border-stone-200 bg-white py-1 shadow-lg shadow-stone-900/5"
        >
          {SNOOZE_OPTIONS.map((opt) => (
            <button
              key={opt.days}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSnooze(opt.days);
              }}
              className="flex w-full items-center px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * One occurrence in a group's schedule. Active occurrences keep the
 * per-row controls (acknowledge, edit date, delete); acknowledged ones
 * render as read-only history.
 */
function OccurrenceRow({
  group,
  row,
  onChanged,
}: {
  group: ReminderGroup<ContractNotification>;
  row: ContractNotification;
  onChanged: () => Promise<void>;
}) {
  const a = useNotificationActions(row, onChanged);
  const acked = Boolean(row.acknowledgedAt);
  // Same due-set as the card header/badge — a live Date.now() check here
  // could contradict the group state computed at grouping time.
  const due = group.due.includes(row);

  return (
    <div
      data-testid="reminder-occurrence"
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-stone-100 py-2 first:border-t-0"
    >
      <div className="flex min-w-0 flex-col gap-0.5 text-xs">
        <span className={acked ? 'text-stone-400' : 'font-medium text-stone-700'}>
          {occurrenceLabel(group, row)}
        </span>
        {a.editing ? (
          <input
            type="date"
            className="rounded border border-stone-300 px-2 py-0.5 text-xs"
            value={a.draftDate}
            onChange={(e) => a.setDraftDate(e.target.value)}
            disabled={a.busy}
          />
        ) : (
          <span className={acked ? 'text-stone-400' : 'text-stone-500'}>
            {formatDate(row.fireAt)}
            <span className="ml-1.5 text-stone-400">({relativeDays(row.fireAt)})</span>
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs">
        {acked ? (
          <span className="text-stone-400" title={row.acknowledgedAt ?? ''}>
            hanterad
          </span>
        ) : due ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
            visas nu
          </span>
        ) : null}
        {!acked &&
          (a.editing ? (
            <>
              <button
                type="button"
                disabled={a.busy}
                onClick={a.saveDate}
                className="rounded bg-stone-900 px-2 py-0.5 text-xs text-white hover:bg-stone-800 disabled:opacity-50"
              >
                Spara
              </button>
              <button
                type="button"
                disabled={a.busy}
                onClick={a.cancelEdit}
                className="rounded border border-stone-300 px-2 py-0.5 text-xs hover:bg-stone-50 disabled:opacity-50"
              >
                Avbryt
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={a.busy}
                onClick={a.acknowledge}
                className="rounded border border-stone-300 px-2 py-0.5 hover:bg-stone-50 disabled:opacity-50"
              >
                Hanterad
              </button>
              <RowMenu
                disabled={a.busy}
                onEditDate={a.startEdit}
                onDelete={a.remove}
              />
            </>
          ))}
      </div>
    </div>
  );
}

/**
 * Per-occurrence action state + API calls.
 */
function useNotificationActions(
  row: ContractNotification,
  onChanged: () => Promise<void>,
) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftDate, setDraftDate] = useState(formatDate(row.fireAt));

  const wrap = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  // Seed the draft when the editor OPENS, not at mount — fireAt can
  // change out-of-band (group snooze) and a mount-time draft would
  // silently write the pre-snooze date back on save.
  const startEdit = () => {
    setDraftDate(formatDate(row.fireAt));
    setEditing(true);
  };
  const saveDate = () =>
    wrap(async () => {
      const fireAt = new Date(`${draftDate}T00:00:00Z`).toISOString();
      await tenantApi.notifications.edit_date({
        notification_id: row.id,
        fire_at: fireAt,
      });
      setEditing(false);
    });
  const cancelEdit = () => {
    setEditing(false);
  };
  const acknowledge = () =>
    wrap(() => tenantApi.notifications.acknowledge({ notification_id: row.id }));
  const remove = () =>
    wrap(() => tenantApi.notifications.delete({ notification_id: row.id }));

  return {
    busy,
    editing,
    startEdit,
    draftDate,
    setDraftDate,
    saveDate,
    cancelEdit,
    acknowledge,
    remove,
  };
}

function RowMenu({
  disabled,
  onEditDate,
  onDelete,
}: {
  disabled: boolean;
  onEditDate: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Fler åtgärder"
        className={
          'inline-flex h-7 w-7 items-center justify-center rounded border transition-colors disabled:opacity-50 ' +
          (open
            ? 'border-stone-600 bg-stone-900 text-white'
            : 'border-stone-300 bg-white text-stone-500 hover:border-stone-400 hover:text-stone-700')
        }
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 z-40 mt-1.5 w-44 overflow-hidden rounded-md border border-stone-200 bg-white py-1 text-left shadow-lg shadow-stone-900/5"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onEditDate();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50"
          >
            <CalendarClock className="h-3.5 w-3.5 text-stone-400" aria-hidden="true" />
            Ändra datum
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void onDelete();
            }}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            Ta bort
          </button>
        </div>
      )}
    </div>
  );
}

function AddCustomModal({
  contractId,
  onClose,
  onAdded,
}: {
  contractId: string;
  onClose: () => void;
  onAdded: () => Promise<void>;
}) {
  const [label, setLabel] = useState('');
  const [date, setDate] = useState(formatDate(new Date(Date.now() + 30 * 86_400_000).toISOString()));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async () => {
    setErr(null);
    if (!label.trim() || !date) {
      setErr('Fyll i både namn och datum.');
      return;
    }
    setBusy(true);
    try {
      await tenantApi.notifications.add_custom({
        contract_id: contractId,
        fire_at: new Date(`${date}T08:00:00Z`).toISOString(),
        label: label.trim(),
      });
      await onAdded();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40"
      onClick={onClose}
    >
      <div
        className="w-[420px] rounded bg-white p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-sm font-medium text-stone-900">Lägg till påminnelse</h3>
        <p className="mt-1 text-xs text-stone-500">
          Egen påminnelse kopplad till det här avtalet. Den visas i klockan
          uppe i topplisten när datumet har passerats.
        </p>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-stone-700">Vad gäller det?</span>
            <input
              type="text"
              maxLength={120}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder='T.ex. "Kontakta hyresgäst om förlängning"'
              className="rounded border border-stone-300 px-2 py-1 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-stone-700">Påminn mig den</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded border border-stone-300 px-2 py-1 text-sm"
            />
          </label>
        </div>
        {err && (
          <div className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700">
            {err}
          </div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded border border-stone-300 px-3 py-1.5 text-xs hover:bg-stone-50 disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onSubmit}
            className="rounded bg-stone-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-stone-800 disabled:opacity-50"
          >
            Spara
          </button>
        </div>
      </div>
    </div>
  );
}
